import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, ToggleLeft, ToggleRight, LayoutList, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import Modal from '../components/common/Modal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { SHIFT_TYPES, shiftTypeLabel } from '../constants/shiftTypes';
import ShiftTypeOrderCard from '../components/roster/ShiftTypeOrder';

// ── Constants ─────────────────────────────────────────────────────────────────

const INDIVIDUAL_DAY_TYPES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_TYPE_LABELS = {
  weekday: 'Mon–Fri',
  weekend: 'Sun & Holidays',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const ALL_TAB_ORDER = ['weekday', 'weekend', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeToMinutes(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function formatDuration(start, end) {
  const mins = timeToMinutes(end) - timeToMinutes(start);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Timeline ──────────────────────────────────────────────────────────────────

const SHIFT_COLORS = [
  '#87CEEB', '#5BA4C8', '#4a90d9', '#7db8e0', '#3d7db3',
  '#a8d8ea', '#2980b9', '#6baed6', '#9ecae1', '#c6dbef',
];

function Timeline({ shifts }) {
  const active = shifts.filter((s) => s.is_active);

  return (
    <div
      style={{
        background: 'var(--bg)',
        borderRadius: 'var(--radius)',
        padding: '16px',
        marginBottom: '20px',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--text-light)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <LayoutList size={14} />
        Daily coverage — active shifts
      </div>

      {/* Hour labels */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.7rem',
          color: 'var(--text-muted)',
          marginBottom: '6px',
          paddingRight: '2px',
        }}
      >
        {[0, 6, 12, 18, 24].map((h) => (
          <span key={h}>{String(h).padStart(2, '0')}:00</span>
        ))}
      </div>

      {/* Backdrop track with grid lines */}
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <div
          style={{
            height: '6px',
            background: 'var(--border)',
            borderRadius: '3px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {[6, 12, 18].map((h) => (
            <div
              key={h}
              style={{
                position: 'absolute',
                left: `${(h / 24) * 100}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                background: 'var(--white)',
                opacity: 0.6,
              }}
            />
          ))}
        </div>
      </div>

      {/* One row per active shift */}
      {active.length === 0 ? (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No active shifts to display.
        </p>
      ) : (
        active.map((shift, i) => {
          const startPct = (timeToMinutes(shift.start_time) / 1440) * 100;
          const widthPct =
            ((timeToMinutes(shift.end_time) - timeToMinutes(shift.start_time)) / 1440) * 100;
          const color = SHIFT_COLORS[i % SHIFT_COLORS.length];

          return (
            <div key={shift.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <div
                style={{
                  width: '110px',
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={shift.name}
              >
                {shift.name}
              </div>
              <div style={{ flex: 1, position: 'relative', height: '18px', background: 'var(--border)', borderRadius: '3px' }}>
                {[6, 12, 18].map((h) => (
                  <div
                    key={h}
                    style={{
                      position: 'absolute',
                      left: `${(h / 24) * 100}%`,
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      background: 'var(--white)',
                      opacity: 0.5,
                      zIndex: 1,
                    }}
                  />
                ))}
                <div
                  style={{
                    position: 'absolute',
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                    top: 0,
                    bottom: 0,
                    background: color,
                    borderRadius: '3px',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                  title={`${shift.start_time} – ${shift.end_time}`}
                >
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: 'var(--text)',
                      whiteSpace: 'nowrap',
                      padding: '0 4px',
                    }}
                  >
                    {shift.start_time}–{shift.end_time}
                  </span>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Form modal ────────────────────────────────────────────────────────────────

const EMPTY_FORM = { name: '', start_time: '', end_time: '', shift_type: '' };

function TemplateForm({ initial, dayType, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.start_time || !form.end_time) {
      setError('Start time and end time are required.');
      return;
    }
    if (form.end_time <= form.start_time) {
      setError('End time must be after start time.');
      return;
    }
    if (!form.shift_type) {
      setError('Shift type is required.');
      return;
    }

    onSave({ ...form, day_type: dayType });
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div
          style={{
            background: 'var(--danger-light)',
            color: 'var(--danger)',
            padding: '10px 14px',
            borderRadius: 'var(--radius)',
            fontSize: '0.875rem',
            marginBottom: '16px',
            border: '1px solid #ffcdd2',
          }}
        >
          {error}
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Day type</label>
        <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text)', fontWeight: 500 }}>
          {DAY_TYPE_LABELS[dayType]}
        </p>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tpl-name">
          Shift name
        </label>
        <input
          id="tpl-name"
          className="form-input"
          type="text"
          value={form.name}
          onChange={set('name')}
          placeholder="e.g. Early – FoH"
          autoFocus
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="tpl-type">
          Shift type
        </label>
        <select
          id="tpl-type"
          className="form-select"
          value={form.shift_type || ''}
          onChange={set('shift_type')}
          required
        >
          <option value="" disabled>— Select shift type —</option>
          {SHIFT_TYPES.map((t) => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
          Used to match staff shift preferences — the name is just a label.
        </p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label" htmlFor="tpl-start">
            Start time
          </label>
          <input
            id="tpl-start"
            className="form-input"
            type="time"
            value={form.start_time}
            onChange={set('start_time')}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="tpl-end">
            End time
          </label>
          <input
            id="tpl-end"
            className="form-input"
            type="time"
            value={form.end_time}
            onChange={set('end_time')}
          />
        </div>
      </div>

      {form.start_time && form.end_time && form.end_time > form.start_time && (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '-8px', marginBottom: '12px' }}>
          Duration: {formatDuration(form.start_time, form.end_time)}
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (
            <>
              <div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
              Saving…
            </>
          ) : (
            'Save shift'
          )}
        </button>
      </div>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function ShiftTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [activeDayTypes, setActiveDayTypes] = useState(['weekday', 'weekend']);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('weekday');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [removingGroup, setRemovingGroup] = useState(false);
  const dropdownRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/shifts/templates');
      setTemplates(res.data.templates);
      setActiveDayTypes(res.data.active_day_types);
    } catch {
      toast.error('Failed to load shift templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openAdd = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingTemplate) {
        const res = await api.put(`/shifts/templates/${editingTemplate.id}`, formData);
        setTemplates((prev) => prev.map((t) => (t.id === res.data.id ? res.data : t)));
        toast.success('Shift template updated.');
      } else {
        const res = await api.post('/shifts/templates', formData);
        setTemplates((prev) => [...prev, res.data]);
        if (!activeDayTypes.includes(formData.day_type)) {
          setActiveDayTypes((prev) => [...prev, formData.day_type]);
        }
        toast.success('Shift template added.');
      }
      closeModal();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg ||
        'Failed to save template.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    const willDeactivate = template.is_active === 1;

    if (willDeactivate) {
      const ok = window.confirm(
        `Deactivate "${template.name}"?\n\nThis shift will no longer appear when creating rosters, but existing shifts are unaffected.`
      );
      if (!ok) return;
    }

    try {
      const res = await api.put(`/shifts/templates/${template.id}`, {
        is_active: !template.is_active,
      });
      setTemplates((prev) => prev.map((t) => (t.id === res.data.id ? res.data : t)));
      toast.success(willDeactivate ? `"${template.name}" deactivated.` : `"${template.name}" activated.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update shift status.');
    }
  };

  const handleRemoveGroup = async () => {
    const ok = window.confirm(
      `This will delete all ${DAY_TYPE_LABELS[tab]} shift templates. Shifts already in published rosters will not be affected. Continue?`
    );
    if (!ok) return;
    setRemovingGroup(true);
    try {
      await api.delete(`/shifts/templates/day-type/${tab}`);
      toast.success(`${DAY_TYPE_LABELS[tab]} template group removed.`);
      setTemplates((prev) => prev.filter((t) => t.day_type !== tab));
      setActiveDayTypes((prev) => prev.filter((dt) => dt !== tab));
      setTab('weekday');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove template group.');
    } finally {
      setRemovingGroup(false);
    }
  };

  // Tabs to show: permanent tabs + any active individual day tabs, in canonical order
  const orderedTabs = ALL_TAB_ORDER.filter(
    (dt) => dt === 'weekday' || dt === 'weekend' || activeDayTypes.includes(dt)
  );
  // Days available to add via the "+" dropdown
  const addableDayTypes = INDIVIDUAL_DAY_TYPES.filter((dt) => !activeDayTypes.includes(dt));

  const tabTemplates = templates.filter((t) => t.day_type === tab);
  const activeCount = tabTemplates.filter((t) => t.is_active).length;

  const modalDayType = editingTemplate ? editingTemplate.day_type : tab;

  const untypedTemplates = templates.filter((t) => t.is_active && !t.shift_type);

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Shift Templates</h1>
          <p className="page-subtitle">
            Define shift types for each day. Individual days override the base templates.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={16} />
          Add shift
        </button>
      </div>

      {/* Display order of shift types (same start time) */}
      <ShiftTypeOrderCard />

      {/* Templates without a shift type — preferences can't be matched for them */}
      {untypedTemplates.length > 0 && (
        <div
          style={{
            display: 'flex', gap: '10px', alignItems: 'flex-start',
            padding: '12px 16px', marginBottom: '16px',
            background: 'var(--warning-light)', border: '1px solid var(--warning)',
            borderRadius: 'var(--radius)', fontSize: '0.875rem',
          }}
        >
          <AlertTriangle size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>These templates have no shift type:</strong>{' '}
            {untypedTemplates.map((t) => `${t.name} (${DAY_TYPE_LABELS[t.day_type] || t.day_type})`).join(', ')}.
            <div style={{ color: 'var(--text-light)', marginTop: '2px' }}>
              Edit them and choose a shift type so staff preferences apply.
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '20px' }}>
        {orderedTabs.map((dt) => (
          <button
            key={dt}
            className={`tab${tab === dt ? ' active' : ''}`}
            onClick={() => setTab(dt)}
          >
            {DAY_TYPE_LABELS[dt]}
          </button>
        ))}

        {/* "+" tab for adding a new individual day group */}
        {addableDayTypes.length > 0 && (
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
              className="tab"
              onClick={() => setDropdownOpen((o) => !o)}
              title="Add individual day template"
              style={{ fontWeight: 700, fontSize: '1.1rem', padding: '6px 14px' }}
            >
              +
            </button>
            {dropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  zIndex: 100,
                  background: 'var(--white)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minWidth: '140px',
                  padding: '4px 0',
                }}
              >
                {addableDayTypes.map((dt) => (
                  <button
                    key={dt}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 16px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: 'var(--text)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                    onClick={() => {
                      setActiveDayTypes((prev) => [...prev, dt]);
                      setTab(dt);
                      setDropdownOpen(false);
                    }}
                  >
                    {DAY_TYPE_LABELS[dt]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <LoadingSpinner label="Loading templates…" />
      ) : (
        <>
          {/* Timeline */}
          <Timeline shifts={tabTemplates} />

          {/* Info note */}
          {tab === 'weekend' && (
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--text-light)',
                marginBottom: '12px',
                padding: '8px 12px',
                background: 'var(--primary-xlight)',
                borderRadius: 'var(--radius)',
              }}
            >
              Applies to Sundays and public holidays. If no individual Saturday template is defined, it also covers Saturdays.
            </p>
          )}
          {INDIVIDUAL_DAY_TYPES.includes(tab) && (
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--text-light)',
                marginBottom: '12px',
                padding: '8px 12px',
                background: 'var(--primary-xlight)',
                borderRadius: 'var(--radius)',
              }}
            >
              Overrides the {['saturday', 'sunday'].includes(tab) ? 'Sun & Holidays' : 'Mon–Fri'} template for {DAY_TYPE_LABELS[tab]}s specifically.
            </p>
          )}

          {/* Summary row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
              <strong style={{ color: 'var(--text)' }}>{activeCount}</strong> active ·{' '}
              <strong style={{ color: 'var(--text)' }}>{tabTemplates.length - activeCount}</strong> inactive
            </p>
          </div>

          {/* Table */}
          {tabTemplates.length === 0 ? (
            <div className="empty-state">
              <LayoutList className="empty-state-icon" />
              <h3>No shifts yet</h3>
              <p>Add a shift template to get started.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tabTemplates.map((t) => (
                    <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.5 }}>
                      <td>
                        <span style={{ fontWeight: 500 }}>{t.name}</span>
                      </td>
                      <td>
                        {t.shift_type
                          ? shiftTypeLabel(t.shift_type)
                          : <span style={{ color: 'var(--warning)', fontWeight: 600 }} title="No shift type — preferences can't be matched">—</span>}
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{t.start_time}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{t.end_time}</td>
                      <td style={{ color: 'var(--text-light)' }}>
                        {formatDuration(t.start_time, t.end_time)}
                      </td>
                      <td>
                        <span className={`badge ${t.is_active ? 'badge-success' : 'badge-secondary'}`}>
                          {t.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEdit(t)}
                            title="Edit shift"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                          <button
                            className={`btn btn-sm ${t.is_active ? 'btn-warning' : 'btn-success'}`}
                            onClick={() => handleToggleActive(t)}
                            title={t.is_active ? 'Deactivate' : 'Activate'}
                            style={{ gap: '4px' }}
                          >
                            {t.is_active ? (
                              <>
                                <ToggleLeft size={14} />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <ToggleRight size={14} />
                                Activate
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Remove group — individual day tabs only */}
          {INDIVIDUAL_DAY_TYPES.includes(tab) && (
            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-danger btn-sm"
                disabled={removingGroup}
                onClick={handleRemoveGroup}
              >
                {removingGroup ? 'Removing…' : `Remove ${DAY_TYPE_LABELS[tab]} template group`}
              </button>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
                {DAY_TYPE_LABELS[tab]}s will fall back to the{' '}
                {['saturday', 'sunday'].includes(tab) ? 'Sun & Holidays' : 'Mon–Fri'} template.
              </p>
            </div>
          )}
        </>
      )}

      {/* Add / Edit modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingTemplate ? 'Edit shift template' : 'Add shift template'}
        size="sm"
      >
        <TemplateForm
          initial={
            editingTemplate
              ? {
                  name: editingTemplate.name,
                  start_time: editingTemplate.start_time,
                  end_time: editingTemplate.end_time,
                  shift_type: editingTemplate.shift_type || '',
                }
              : EMPTY_FORM
          }
          dayType={modalDayType}
          onSave={handleSave}
          onClose={closeModal}
          saving={saving}
        />
      </Modal>

      {/* Special Events section */}
      <SpecialEventsSection />
    </div>
  );
}

// ── SpecialEventsSection ──────────────────────────────────────────────────────

const ORDINAL = ['1st', '2nd', '3rd', '4th', '5th'];
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EMPTY_EVENT_FORM = {
  name: '',
  nth: 1,
  weekday: 2,
  start_time: '',
  end_time: '',
  slots: 1,
  min_slots_warning: 0,
  priority_role_id: '',
  rotation_skip_next_month: false,
  participant_mode: 'slots',
  ignore_availability_blocks: false,
};

function SpecialEventsSection() {
  const [events, setEvents] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/special-events'), api.get('/om/roles')])
      .then(([evRes, roleRes]) => {
        setEvents(evRes.data || []);
        setRoles((roleRes.data || []).filter(r => r.is_active));
      })
      .catch(() => toast.error('Failed to load special events.'))
      .finally(() => setLoading(false));
  }, []);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_EVENT_FORM);
    setModalOpen(true);
  }

  function openEdit(ev) {
    setEditing(ev);
    setForm({
      name: ev.name,
      nth: ev.nth,
      weekday: ev.weekday,
      start_time: ev.start_time,
      end_time: ev.end_time,
      slots: ev.slots,
      min_slots_warning: ev.min_slots_warning,
      priority_role_id: ev.priority_role_id ?? '',
      rotation_skip_next_month: Boolean(ev.rotation_skip_next_month),
      participant_mode: ev.participant_mode || 'slots',
      ignore_availability_blocks: Boolean(ev.ignore_availability_blocks),
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
  }

  function setField(k, v) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.start_time || !form.end_time) {
      toast.error('Name, start time, and end time are required.');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      nth: Number(form.nth),
      weekday: Number(form.weekday),
      slots: Number(form.slots),
      min_slots_warning: Number(form.min_slots_warning),
      priority_role_id: form.priority_role_id ? Number(form.priority_role_id) : null,
      rotation_skip_next_month: form.rotation_skip_next_month ? 1 : 0,
      ignore_availability_blocks: form.ignore_availability_blocks ? 1 : 0,
    };
    try {
      if (editing) {
        const res = await api.put(`/special-events/${editing.id}`, payload);
        setEvents(prev => prev.map(ev => ev.id === editing.id ? res.data : ev));
        toast.success('Event updated.');
      } else {
        const res = await api.post('/special-events', payload);
        setEvents(prev => [...prev, res.data]);
        toast.success('Event created.');
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save event.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(ev) {
    const action = ev.is_active ? 'deactivate' : 'activate';
    try {
      const res = await api.post(`/special-events/${ev.id}/${action}`);
      setEvents(prev => prev.map(e => e.id === ev.id ? res.data : e));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update event.');
    }
  }

  return (
    <div style={{ borderTop: '2px solid var(--border)', marginTop: '40px', paddingTop: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>Special Events</h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', margin: '4px 0 0' }}>
            Recurring events with dedicated roster slots (e.g. monthly deep-clean).
          </p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={16} /> Add event
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : events.length === 0 ? (
        <div className="empty-state">
          <p>No special events defined yet.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Recurrence</th>
                <th>Time</th>
                <th>Slots</th>
                <th>Min. warning</th>
                <th>Priority role</th>
                <th>Rotation</th>
                <th>Availability blocks</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.id} style={{ opacity: ev.is_active ? 1 : 0.5 }}>
                  <td style={{ fontWeight: 500 }}>{ev.name}</td>
                  <td>{ORDINAL[ev.nth - 1] || String(ev.nth)} {WEEKDAY_NAMES[ev.weekday]}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{ev.start_time}–{ev.end_time}</td>
                  <td>{ev.participant_mode === 'all_om_role_holders' ? 'All OM role holders' : ev.slots}</td>
                  <td>{ev.min_slots_warning > 0 ? ev.min_slots_warning : '—'}</td>
                  <td>{ev.priority_role_name || '—'}</td>
                  <td>{ev.rotation_skip_next_month ? 'Yes' : 'No'}</td>
                  <td>{ev.participant_mode === 'all_om_role_holders' ? '—' : ev.ignore_availability_blocks ? 'Ignored' : 'Apply'}</td>
                  <td>
                    <span className={`badge ${ev.is_active ? 'badge-success' : 'badge-secondary'}`}>
                      {ev.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(ev)} title="Edit">
                        <Pencil size={14} />
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleToggle(ev)}
                        title={ev.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {ev.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editing ? 'Edit special event' : 'New special event'}
        size="sm"
      >
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input className="form-input" value={form.name} onChange={e => setField('name', e.target.value)} required />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Which occurrence</label>
              <select className="form-select" value={form.nth} onChange={e => setField('nth', Number(e.target.value))}>
                {ORDINAL.map((o, i) => <option key={i + 1} value={i + 1}>{o}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Weekday</label>
              <select className="form-select" value={form.weekday} onChange={e => setField('weekday', Number(e.target.value))}>
                {WEEKDAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Start time</label>
              <input className="form-input" type="time" value={form.start_time} onChange={e => setField('start_time', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">End time</label>
              <input className="form-input" type="time" value={form.end_time} onChange={e => setField('end_time', e.target.value)} required />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Participants</label>
            <select className="form-select" value={form.participant_mode} onChange={e => setField('participant_mode', e.target.value)}>
              <option value="slots">Fixed number of slots</option>
              <option value="all_om_role_holders">All OM role holders</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Slots</label>
              <input
                className="form-input"
                type="number"
                min="1"
                value={form.slots}
                disabled={form.participant_mode === 'all_om_role_holders'}
                onChange={e => setField('slots', Number(e.target.value))}
              />
              {form.participant_mode === 'all_om_role_holders' && (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  One slot per active OM role holder.
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Min. slots warning</label>
              <input className="form-input" type="number" min="0" value={form.min_slots_warning} onChange={e => setField('min_slots_warning', Number(e.target.value))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Priority role (for slot 1)</label>
            <select className="form-select" value={form.priority_role_id} onChange={e => setField('priority_role_id', e.target.value)}>
              <option value="">— none —</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.rotation_skip_next_month}
                onChange={e => setField('rotation_skip_next_month', e.target.checked)}
              />
              Skip next month (rotation)
            </label>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Users who worked this event will be excluded from the following month's assignment.
            </p>
          </div>

          {form.participant_mode !== 'all_om_role_holders' && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.ignore_availability_blocks}
                  onChange={e => setField('ignore_availability_blocks', e.target.checked)}
                />
                Ignore availability blocks (only vacation, absences and sickness block)
              </label>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                For evening events: blocked days, weekends, weekend rhythm, PMS days and morning half-days do not
                prevent assignment. Afternoon half-days and admin weekday blocks still apply. People without a
                block on that day are picked first.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create event'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default ShiftTemplatesPage;
